/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect, useRef, useState } from "react";
import type { FunctionComponent, JSX } from "react";
import type { TChannel } from "@tivocorp/data-domain-lib";

import { Log } from "../../../model/logging";
import type { TCarouselItem } from "../../../model/types";
import { ChannelsInteractor } from "../../../model/interactor";
import { DeviceModel } from "../../../model/device";
import { formatTilePreview } from "./TilePreviewFormatter";
import { TilePreviewTemplate } from "./TilePreviewTemplate";
import type { TilePreviewData } from "./TilePreviewFormatter";

import "./TilePreview.scss";

type TilePreviewProps = TCarouselItem & {
  template: TilePreviewTemplate;
};

/**
 * Templates whose styling differs between browser and SmartTV.
 * Only these receive the --browser / --smarttv platform modifier class.
 * Templates absent from this set are platform-agnostic (SmartTV-only or
 * identical across platforms) and must not carry a platform class, so that
 * a future bare `.tile-preview--smarttv` / `.tile-preview--browser` rule
 * can never leak into them.
 */
const PLATFORM_SCOPED_TEMPLATES: ReadonlySet<TilePreviewTemplate> = new Set([
  TilePreviewTemplate.MOVIE_POSTER_HORIZONTAL,
  TilePreviewTemplate.SHOW_POSTER_HORIZONTAL,
  TilePreviewTemplate.LOGO_PIP_LANDSCAPE_PREVIEW,
  TilePreviewTemplate.SERIES_RECORDING_HORIZONTAL,
  TilePreviewTemplate.MOVIE_RECORDING_HORIZONTAL
]);

/**
 * TilePreview — renders tile info block for a focused content tile.
 */
const TilePreview: FunctionComponent<TilePreviewProps> = (
  item: TilePreviewProps
): JSX.Element => {
  const TAG: string = "TilePreview";
  const { template, ...carouselItem } = item;

  /**
   * Determine if rendering on a browser platform.
   * Browser types: Chrome, Safari, Firefox, Edge, Opera.
   */
  const isBrowser = (): boolean => !DeviceModel.isTvDevice();

  /**
   * Get the template-specific CSS class modifier.
   * Converts template enum to kebab-case class name.
   */
  const getTemplateClassName = (): string => {
    const templateToClass: Record<TilePreviewTemplate, string> = {
      [TilePreviewTemplate.BROWSER_LANDSCAPE_HORIZONTAL_PREVIEW]:
        "tile-preview--browser-landscape-horizontal-preview",
      [TilePreviewTemplate.MOVIE_POSTER_HORIZONTAL]:
        "tile-preview--movie-poster-horizontal",
      [TilePreviewTemplate.SHOW_POSTER_HORIZONTAL]:
        "tile-preview--show-poster-horizontal",
      [TilePreviewTemplate.LOGO_PIP_LANDSCAPE_PREVIEW]:
        "tile-preview--logo-pip-landscape-preview",
      [TilePreviewTemplate.LIVE_PIP_LANDSCAPE_PREVIEW]:
        "tile-preview--live-pip-landscape-preview",
      [TilePreviewTemplate.LIVE_LANDSCAPE_HORIZONTAL_PREVIEW]:
        "tile-preview--live-landscape-horizontal-preview",
      [TilePreviewTemplate.NON_LIVE_LANDSCAPE_HORIZONTAL_PREVIEW]:
        "tile-preview--non-live-landscape-horizontal-preview",
      [TilePreviewTemplate.SERIES_RECORDING_HORIZONTAL]:
        "tile-preview--series-recording-horizontal",
      [TilePreviewTemplate.MOVIE_RECORDING_HORIZONTAL]:
        "tile-preview--movie-recording-horizontal"
    };
    return templateToClass[template];
  };

  /**
   * Get platform modifier class for CSS styling.
   * Adds --browser or --smarttv modifier for platform-specific styling.
   */
  const getPlatformClass = (): string => {
    return isBrowser() ? "tile-preview--browser" : "tile-preview--smarttv";
  };

  /**
   * Builds the root class list. The platform modifier is appended only for
   * templates that actually have platform-specific styling.
   */
  const getRootClassName = (): string => {
    const classes: string[] = ["tile-preview", getTemplateClassName()];
    if (PLATFORM_SCOPED_TEMPLATES.has(template)) {
      classes.push(getPlatformClass());
    }
    return classes.join(" ");
  };

  const [resolvedChannelName, setResolvedChannelName] = useState<string>(
    carouselItem.channelName !== undefined && carouselItem.channelName !== null
      ? carouselItem.channelName
      : ""
  );
  const [resolvedChannelNumber, setResolvedChannelNumber] = useState<string>(
    carouselItem.channelNumber !== undefined &&
      carouselItem.channelNumber !== null
      ? carouselItem.channelNumber
      : ""
  );

  const [visibleId, setVisibleId] = useState<string>("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect((): (() => void) => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout((): void => {
      setVisibleId(carouselItem.id);
    }, 150);

    return (): void => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [carouselItem.id]);

  useEffect((): void => {
    if (carouselItem.stationId === undefined || carouselItem.stationId === "") {
      return;
    }

    new ChannelsInteractor()
      .getChannelByStationId(carouselItem.stationId)
      .then((channel: TChannel | null): void => {
        if (channel === null) {
          return;
        }
        if (channel.name !== undefined && channel.name !== "") {
          setResolvedChannelName(channel.name);
        } else if (channel.callSign !== undefined && channel.callSign !== "") {
          setResolvedChannelName(channel.callSign);
        }
        if (
          channel.channelNumber !== undefined &&
          channel.channelNumber !== ""
        ) {
          setResolvedChannelNumber(channel.channelNumber);
        }
      })
      .catch((error: unknown): void => {
        Log.e(TAG, "useEffect — getChannelByStationId()", error);
      });
  }, [carouselItem.stationId]);

  const enrichedItem: TCarouselItem = {
    ...carouselItem,
    channelName:
      resolvedChannelName !== ""
        ? resolvedChannelName
        : carouselItem.channelName,
    channelNumber:
      resolvedChannelNumber !== ""
        ? resolvedChannelNumber
        : carouselItem.channelNumber
  };

  const preview: TilePreviewData = formatTilePreview(enrichedItem, template);
  const subtitleText: string =
    preview.subtitleInfo !== undefined && preview.subtitleInfo !== null
      ? preview.subtitleInfo
      : "";

  const isPosterLayout: boolean =
    template === TilePreviewTemplate.MOVIE_POSTER_HORIZONTAL ||
    template === TilePreviewTemplate.SHOW_POSTER_HORIZONTAL;
  const isBrowserContentLayout: boolean =
    template === TilePreviewTemplate.BROWSER_LANDSCAPE_HORIZONTAL_PREVIEW;
  const isPipLayout: boolean =
    template === TilePreviewTemplate.LOGO_PIP_LANDSCAPE_PREVIEW ||
    template === TilePreviewTemplate.LIVE_PIP_LANDSCAPE_PREVIEW;
  const hasRatingBadge: boolean =
    carouselItem.ratingBadge !== undefined && carouselItem.ratingBadge !== "";
  const hasSubtitleInfo: boolean = subtitleText !== "";
  const hasMetaInfo: boolean =
    preview.metaInfo !== undefined && preview.metaInfo !== "";

  if (visibleId !== carouselItem.id) {
    return <div className="tile-preview" />;
  }

  // ── Poster (title + subtitle only, platform-scoped) ──
  if (isPosterLayout) {
    return (
      <div className={getRootClassName()}>
        <h4 className="tile-preview__title">{preview.title}</h4>
        {hasSubtitleInfo && (
          <p className="tile-preview__subtitle-info">{subtitleText}</p>
        )}
      </div>
    );
  }

  // ── Browser landscape (4-line browser layout) ──
  if (isBrowserContentLayout) {
    return (
      <div className={getRootClassName()}>
        <h4 className="tile-preview__title">{preview.title}</h4>

        {hasSubtitleInfo && (
          <p className="tile-preview__subtitle-info">{subtitleText}</p>
        )}

        {(hasRatingBadge || preview.channelInfo !== undefined) && (
          <div className="tile-preview__meta-info tile-preview__meta-info--browser-line3">
            {hasRatingBadge && (
              <span className="tile-preview__rating-badge">
                {carouselItem.ratingBadge}
              </span>
            )}
            {preview.channelInfo !== undefined && (
              <span className="tile-preview__channel-info">
                {preview.channelInfo}
              </span>
            )}
          </div>
        )}

        {preview.dayAndTimeRange !== undefined &&
          preview.dayAndTimeRange !== "" && (
            <p className="tile-preview__day-time">{preview.dayAndTimeRange}</p>
          )}
      </div>
    );
  }

  // ── PIP (title + subtitle only; logo PIP is platform-scoped) ──
  if (isPipLayout) {
    return (
      <div className={getRootClassName()}>
        <h4 className="tile-preview__title">{preview.title}</h4>
        {hasSubtitleInfo && (
          <p className="tile-preview__subtitle-info">{subtitleText}</p>
        )}
      </div>
    );
  }

  // ── Default: live / non-live landscape + recordings ──
  return (
    <div className={getRootClassName()}>
      <h4 className="tile-preview__title">{preview.title}</h4>

      {hasSubtitleInfo && (
        <p className="tile-preview__subtitle-info">{subtitleText}</p>
      )}

      {hasMetaInfo && (
        <div className="tile-preview__meta-info">
          {hasRatingBadge && (
            <span className="tile-preview__rating-badge">
              {carouselItem.ratingBadge}
            </span>
          )}
          {preview.channelInfo && (
            <span className="tile-preview__channel-info">
              {preview.channelInfo}
            </span>
          )}
          {preview.timeRange && (
            <span className="tile-preview__time-range">
              {preview.timeRange}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TilePreview;
